import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const datasetUrl = this.dataset.productUrl?.trim();
    if (datasetUrl) {
      const url = new URL(datasetUrl, window.location.origin);

      if (!url.searchParams.has('variant')) {
        const selectedVariantId = this.#getSelectedVariantId();
        if (selectedVariantId) {
          url.searchParams.set('variant', selectedVariantId);
        }
      }

      return url.toString();
    }

    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    variantPicker?.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const trigger =
      event.target instanceof Element
        ? event.target.closest('[data-pi-upsell-quick-view-btn], .quick-add__button, button, a')
        : null;
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (dialogComponent instanceof QuickAddDialog && trigger instanceof HTMLElement) {
      dialogComponent.setLastTrigger(trigger);
    }

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
      this.#openQuickAddModal();
      return;
    }

    this.#openQuickAddModal();
  };

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
    requestAnimationFrame(() => dialogComponent.focusInitialElement());
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
    this.#restructureQuickViewLayout(modalContent);
  }

  /**
   * Subtitle line (vendor / type) for Spanx-style header.
   * @param {Element} [productGrid]
   * @returns {string}
   */
  #getProductSubtitle(productGrid) {
    const fromDataset = this.dataset.productVendor?.trim();
    if (fromDataset) return fromDataset;

    const typeFromGrid = productGrid?.querySelector('[data-product-type]')?.getAttribute('data-product-type')?.trim();
    if (typeFromGrid) return typeFromGrid;

    const dialog = document.getElementById('quick-add-dialog');
    if (dialog instanceof HTMLElement) {
      return dialog.dataset.shopLabel?.trim() || '';
    }

    return '';
  }

  /**
   * Vertical Quick View layout (vendor → title → price → options → CTA → details).
   * @param {Element} modalContent
   */
  #restructureQuickViewLayout(modalContent) {
    if (modalContent.querySelector('.quick-view__shell')) {
      this.#refreshQuickViewLinks(modalContent);
      return;
    }

    const productUrl = this.productPageUrl;
    const subtitle = this.#getProductSubtitle(modalContent);
    const titleText =
      this.dataset.productTitle?.trim() ||
      modalContent.querySelector('.view-product-title a')?.textContent?.trim() ||
      modalContent.querySelector('.quick-add-modal__title')?.textContent?.trim() ||
      '';

    const price = modalContent.querySelector('product-price');
    const variantPicker = modalContent.querySelector('variant-picker');
    const buyButtons =
      modalContent.querySelector('.buy-buttons-block') ||
      modalContent.querySelector('product-form-component');

    modalContent.querySelector('.product-information__media')?.remove();

    const shell = document.createElement('div');
    shell.className = 'quick-view__shell';

    const header = document.createElement('div');
    header.className = 'quick-view__header';
    const vendor = document.createElement('div');
    vendor.className = 'quick-view__vendor';
    vendor.textContent = subtitle;
    header.appendChild(vendor);
    shell.appendChild(header);

    const titleHeading = document.createElement('h2');
    titleHeading.className = 'quick-view__title';
    const titleLink = document.createElement('a');
    titleLink.href = productUrl;
    titleLink.textContent = titleText;
    titleHeading.appendChild(titleLink);
    shell.appendChild(titleHeading);

    if (price) {
      const priceWrap = document.createElement('div');
      priceWrap.className = 'quick-view__price';
      priceWrap.appendChild(price);
      shell.appendChild(priceWrap);
    }

    if (variantPicker) {
      const optionsWrap = document.createElement('div');
      optionsWrap.className = 'quick-view__options';
      optionsWrap.appendChild(variantPicker);
      shell.appendChild(optionsWrap);
    }

    if (buyButtons) {
      const actionsWrap = document.createElement('div');
      actionsWrap.className = 'quick-view__actions';
      actionsWrap.appendChild(buyButtons);
      shell.appendChild(actionsWrap);
    }

    const dialog = document.getElementById('quick-add-dialog');
    const moreDetailsLabel =
      dialog instanceof HTMLElement && dialog.dataset.moreDetailsLabel
        ? dialog.dataset.moreDetailsLabel
        : 'View more details';

    const moreDetails = document.createElement('a');
    moreDetails.className = 'quick-view__details-link';
    moreDetails.href = productUrl;
    moreDetails.textContent = moreDetailsLabel;

    modalContent.replaceChildren(shell, moreDetails);
  }

  /**
   * @param {Element} modalContent
   */
  #refreshQuickViewLinks(modalContent) {
    const productUrl = this.productPageUrl;
    const titleLink = modalContent.querySelector('.quick-view__title a');
    const detailsLink = modalContent.querySelector('.quick-view__details-link');

    if (titleLink instanceof HTMLAnchorElement) titleLink.href = productUrl;
    if (detailsLink instanceof HTMLAnchorElement) detailsLink.href = productUrl;
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();
  /** @type {HTMLElement | null} */
  #lastTrigger = null;

  /**
   * @param {HTMLElement} element
   */
  setLastTrigger(element) {
    this.#lastTrigger = element;
  }

  focusInitialElement() {
    const closeButton = this.querySelector('.quick-add-modal__close');
    if (closeButton instanceof HTMLElement) {
      closeButton.focus();
      return;
    }

    const modalContent = document.getElementById('quick-add-modal-content');
    const firstFocusable = modalContent?.querySelector(
      'button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled])'
    );
    if (firstFocusable instanceof HTMLElement) {
      firstFocusable.focus();
    }
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );

    if (!anchorElement) return;

    const quickViewTitle = /** @type {HTMLAnchorElement | null} */ (this.querySelector('.quick-view__title a'));
    const moreDetailsLink = /** @type {HTMLAnchorElement | null} */ (this.querySelector('.quick-view__details-link'));

    if (quickViewTitle) quickViewTitle.href = anchorElement.href;
    if (moreDetailsLink) moreDetailsLink.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const trigger = this.#lastTrigger;
    this.#lastTrigger = null;

    if (trigger?.isConnected) {
      requestAnimationFrame(() => trigger.focus());
    }

    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
